/**
 * ImportBrandingDialog — visual preview + confirm-and-apply for a branding
 * bundle (Phase 1). The bundle is parsed client-side; this dialog shows what it
 * contains (app name, color swatches, logo/favicon images, login look, renamed
 * labels, theme names) so an admin can confirm before overwriting branding.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TERM_AREAS } from '@/lib/branding';

const KNOWN_BRANDING_KEYS = new Set([
  'app_name', 'logo_url', 'logo_url_dark', 'favicon_url',
  'primary_color', 'accent_color', 'sidebar_bg',
  'primary_color_dark', 'accent_color_dark', 'sidebar_bg_dark',
  'cover_bg_color', 'cover_bg_image_url',
  'login_tagline', 'login_bg_image_url', 'root_page', 'terms',
  'email_reset_subject', 'email_reset_intro',
  'email_invite_subject', 'email_invite_intro', 'invite_reminder_days',
]);

const Swatch = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5" data-testid={`preview-swatch-${label}`}>
      <span className="w-5 h-5 rounded-md border border-border/60" style={{ background: value }} />
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
};

const ImgChip = ({ label, src, dark }) => {
  if (!src) return null;
  return (
    <div className="flex flex-col items-center gap-1" data-testid={`preview-image-${label}`}>
      <div className={'w-16 h-16 rounded-lg border border-border/60 flex items-center justify-center overflow-hidden ' + (dark ? 'bg-zinc-900' : 'bg-white')}>
        <img src={src} alt={label} className="max-w-full max-h-full object-contain" />
      </div>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
};

export default function ImportBrandingDialog({ open, onOpenChange, bundle, currentAppName, themesOnly = false, onThemesOnlyChange, onConfirm, applying }) {
  if (!bundle) return null;
  const b = bundle.branding || {};
  const themes = bundle.themes || [];
  const termAreas = TERM_AREAS || [];
  const renames = [];
  if (b.terms && typeof b.terms === 'object') {
    for (const area of termAreas) {
      const t = b.terms[area.key];
      if (t && (t.plural || t.singular)) renames.push({ label: area.label, value: t.plural || t.singular });
    }
  }
  const emailBits = [];
  if (b.email_reset_subject) emailBits.push({ label: 'Reset subject', value: b.email_reset_subject });
  if (b.email_invite_subject) emailBits.push({ label: 'Invite subject', value: b.email_invite_subject });
  if (b.invite_reminder_days != null && b.invite_reminder_days !== '') emailBits.push({ label: 'Invite reminder', value: `${b.invite_reminder_days} day(s)` });
  // Anything in the bundle's branding we don't recognize on this version.
  const unknownKeys = Object.keys(b).filter((k) => !KNOWN_BRANDING_KEYS.has(k));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="import-branding-dialog">
        <DialogHeader>
          <DialogTitle>Import branding</DialogTitle>
          <DialogDescription>
            Review what this bundle contains. Applying it overwrites the current branding for this tenant.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] pr-3">
          <div className="space-y-4">
            {/* App name before/after */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Application name</div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground line-through" data-testid="preview-appname-before">{currentAppName || '—'}</span>
                <span className="text-muted-foreground">→</span>
                <span className="font-semibold text-foreground" data-testid="preview-appname-after">{b.app_name || '—'}</span>
              </div>
            </div>

            {/* Images */}
            {(b.logo_url || b.logo_url_dark || b.favicon_url || b.login_bg_image_url || b.cover_bg_image_url) && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Images</div>
                <div className="flex flex-wrap gap-3">
                  <ImgChip label="Logo" src={b.logo_url} />
                  <ImgChip label="Dark logo" src={b.logo_url_dark} dark />
                  <ImgChip label="Favicon" src={b.favicon_url} />
                  <ImgChip label="Login bg" src={b.login_bg_image_url} dark />
                  <ImgChip label="Cover bg" src={b.cover_bg_image_url} dark />
                </div>
              </div>
            )}

            {/* Colors */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Colors</div>
              <div className="flex flex-wrap gap-3">
                <Swatch label="primary" value={b.primary_color} />
                <Swatch label="accent" value={b.accent_color} />
                <Swatch label="sidebar" value={b.sidebar_bg} />
                <Swatch label="primary (dark)" value={b.primary_color_dark} />
                <Swatch label="accent (dark)" value={b.accent_color_dark} />
                <Swatch label="sidebar (dark)" value={b.sidebar_bg_dark} />
              </div>
            </div>

            {/* Renamed labels */}
            {renames.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Renamed labels</div>
                <div className="flex flex-wrap gap-1.5">
                  {renames.map((r) => (
                    <span key={r.label} className="text-[10px] px-2 py-0.5 rounded-md bg-muted text-foreground border border-border/50" data-testid={`preview-term-${r.label}`}>
                      {r.label}: <span className="font-medium">{r.value}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Email branding */}
            {emailBits.length > 0 && (
              <div data-testid="preview-email-branding">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Email branding</div>
                <div className="flex flex-wrap gap-1.5">
                  {emailBits.map((e) => (
                    <span key={e.label} className="text-[10px] px-2 py-0.5 rounded-md bg-muted text-foreground border border-border/50">
                      {e.label}: <span className="font-medium">{e.value}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Themes */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Custom themes ({themes.length})</div>
              {themes.length === 0 ? (
                <div className="text-[11px] text-muted-foreground">None</div>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="preview-themes">
                  {themes.map((t, i) => (
                    <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted border border-border/50">
                      <div className="flex -space-x-0.5">
                        {(t.colors || []).slice(0, 5).map((c, j) => (
                          <span key={j} className="w-3 h-3 rounded-full border border-background" style={{ background: c }} />
                        ))}
                      </div>
                      <span className="text-[10px] text-foreground">{t.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {bundle.default_theme_name && (
                <div className="text-[10px] text-muted-foreground mt-1" data-testid="preview-default-theme">
                  Default theme: <span className="text-foreground font-medium">{bundle.default_theme_name}</span>
                </div>
              )}
            </div>

            {/* Source / forward-compat note */}
            <div className="text-[10px] text-muted-foreground border-t border-border/40 pt-2">
              {bundle.source_env ? <>From <span className="text-foreground">{bundle.source_env}</span>. </> : null}
              {bundle.exported_at ? <>Exported {new Date(bundle.exported_at).toLocaleString()}.</> : null}
            </div>
            {unknownKeys.length > 0 && (
              <div className="flex items-start gap-1.5 text-[10px] text-amber-500" data-testid="preview-unknown-keys">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>This bundle also carries settings this version doesn't apply yet ({unknownKeys.join(', ')}). They'll be ignored.</span>
              </div>
            )}
          </div>
        </ScrollArea>

        <label className="flex items-start gap-2 mt-2 cursor-pointer select-none" data-testid="import-themes-only-toggle">
          <input
            type="checkbox"
            checked={themesOnly}
            onChange={(e) => onThemesOnlyChange && onThemesOnlyChange(e.target.checked)}
            className="mt-0.5 accent-primary"
            data-testid="import-themes-only-checkbox"
          />
          <span className="text-[11px] text-muted-foreground">
            <span className="text-foreground font-medium">Add themes only — keep my current branding.</span> Imports the {themes.length} theme{themes.length === 1 ? '' : 's'} above without changing your app name, colors, logos, labels or default theme.
          </span>
        </label>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={applying} data-testid="import-branding-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={applying} className="rounded-lg" data-testid="import-branding-confirm">
            <CheckCircle2 className="w-4 h-4 mr-1" /> {applying ? 'Applying…' : (themesOnly ? 'Add themes' : 'Apply branding')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
